import type { UnlockMediaPasswordStatus } from './state/sub_stores/playable_media';
import type { SyncError } from './sync/client/sync_error';

export class PlayerTimeMarkerClickEvent extends CustomEvent<{
  segmentIds: string[];
}> {
  constructor(segmentIds: string[]) {
    super('timelineMarkerClick', { detail: { segmentIds } });
  }
}

export class PlayerFullscreenPrevEvent extends CustomEvent<void> {
  constructor() {
    super('fullscreenPrev');
  }
}

export class PlayerFullscreenNextEvent extends CustomEvent<void> {
  constructor() {
    super('fullscreenNext');
  }
}

export class PlayerFullscreenExitEvent extends CustomEvent<void> {
  constructor() {
    super('fullscreenExit');
  }
}

export class PlayerFullscreenEnterEvent extends CustomEvent<void> {
  constructor() {
    super('fullscreenEnter');
  }
}

export class PlayerSyncErrorEvent extends CustomEvent<{ error: SyncError }> {
  constructor(error: SyncError) {
    super('syncError', { detail: { error } });
  }
}

export class PlayerSidebarToggleEvent extends CustomEvent<void> {
  constructor() {
    super('sidebarToggle');
  }
}

export class PlayerRetryMediaEvent extends CustomEvent<void> {
  constructor() {
    super('retryMedia');
  }
}

type PlayerMediaPasswordEventDetails = {
  updatePassword: (password: string) => void;
  status: UnlockMediaPasswordStatus;
};

export class PlayerMediaPasswordEvent extends CustomEvent<PlayerMediaPasswordEventDetails> {
  constructor(
    updatePassword: PlayerMediaPasswordEventDetails['updatePassword'],
    status: PlayerMediaPasswordEventDetails['status'],
  ) {
    super('mediaPassword', { detail: { updatePassword, status } });
  }
}

export interface PlayerEventMap {
  timelineMarkerClick: CustomEvent<{ segmentIds: string[] }>;
  fullscreenPrev: CustomEvent;
  fullscreenNext: CustomEvent;
  fullscreenExit: CustomEvent;
  fullscreenEnter: CustomEvent;
  syncError: CustomEvent<{ error: SyncError }>;
  sidebarToggle: CustomEvent;
  mediaPassword: CustomEvent<PlayerMediaPasswordEventDetails>;
  retryMedia: CustomEvent;
}

interface PlayerEventTarget extends EventTarget {
  addEventListener<K extends keyof PlayerEventMap>(
    type: K,
    listener: (event: PlayerEventMap[K]) => void,
    options?: AddEventListenerOptions | boolean,
  ): void;

  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: AddEventListenerOptions | boolean,
  ): void;

  removeEventListener<K extends keyof PlayerEventMap>(
    type: K,
    listener: (event: PlayerEventMap[K]) => void,
    options?: EventListenerOptions | boolean,
  ): void;

  removeEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: EventListenerOptions | boolean,
  ): void;
}

// biome-ignore lint/plugin: retypes the built-in EventTarget constructor with the player's typed event-listener overloads; EventTarget's native signatures cannot be narrowed to these at runtime.
const PlayerTypedEventTarget = EventTarget as {
  new (): PlayerEventTarget;
  prototype: PlayerEventTarget;
};

export class PlayerEvents extends PlayerTypedEventTarget {}
